import type {
  ApiRequestBody,
  ApiResponseBody,
  ApiSearchQuery,
} from "@/lib/api/api-type-helper";
import { apiClient } from "@/lib/api/client";
import { getDataOrThrowApiError } from "@/lib/api/error";
import { downloadFile } from "@/lib/api/utils";
import { queryClient } from "@/lib/queryClient";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";

/**
 * Keys for mutation
 */
const EQUIPMENT_API_KEYS = {
  all: ["equipment"] as const,
  list: (params: GetEquipmentListQuery) =>
    [...EQUIPMENT_API_KEYS.all, "list", params] as const,
  count: (params: GetEquipmentCountQuery) =>
    [...EQUIPMENT_API_KEYS.all, "count", params] as const,
  detail: (equipmentId: string) =>
    [...EQUIPMENT_API_KEYS.all, "detail", equipmentId] as const,
  export: ["export"] as const,
} as const;

export type EquipmentCategory = NonNullable<GetEquipmentCountQuery["category"]>;
export const EquipmentCategory = {
  ALL: "ALL",
  DISPOSED: "DISPOSED",
  PENDING_DISPOSAL: "PENDING_DISPOSAL",
} as const satisfies Record<EquipmentCategory, string>;

export type EquipmentOrderBy = NonNullable<GetEquipmentListQuery["orderBy"]>;
export const EquipmentOrderBy = {
  NAME: "NAME",
  EQUIPMENT_TYPE: "EQUIPMENT_TYPE",
  MODEL: "MODEL",
  SERIAL_NUMBER: "SERIAL_NUMBER",
  ASSET_TAG: "ASSET_TAG",
  CREATED_AT: "CREATED_AT",
} as const satisfies Record<EquipmentOrderBy, string>;

export type EquipmentControlType = NonNullable<
  GetEquipmentResponse["controlType"]
>;
export const EquipmentControlType = {
  INVENTORY_MANAGEMENT: "INVENTORY_MANAGEMENT",
  DISTRIBUTION_LOG_MANAGEMENT: "DISTRIBUTION_LOG_MANAGEMENT",
  UNMANAGED: "UNMANAGED",
} as const satisfies Record<EquipmentControlType, string>;

export type EquipmentUsageStatus = NonNullable<
  NonNullable<GetEquipmentResponse["assignment"]>["usageStatus"]
>;
export const EquipmentUsageStatus = {
  IN_USE: "IN_USE",
  RETURNED: "RETURNED",
  UNDER_REPAIR: "UNDER_REPAIR",
  DISPOSED: "DISPOSED",
  STORED: "STORED",
  SCHEDULED_FOR_LENDING: "SCHEDULED_FOR_LENDING",
} as const satisfies Record<EquipmentUsageStatus, string>;

export type EquipmentUsageType = NonNullable<
  NonNullable<GetEquipmentResponse["assignment"]>["usageType"]
>;
export const EquipmentUsageType = {
  PERSONAL_ASSIGNMENT: "PERSONAL_ASSIGNMENT",
  SHARED_USE: "SHARED_USE",
  SHORT_TERM_LENDING: "SHORT_TERM_LENDING",
  WORK_FROM_HOME: "WORK_FROM_HOME",
} as const satisfies Record<EquipmentUsageType, string>;

export type EquipmentPlacementType = NonNullable<
  NonNullable<GetEquipmentResponse["placement"]>["placementType"]
>;
export const EquipmentPlacementType = {
  STORED: "STORED",
  FIXED: "FIXED",
  FREE: "FREE",
  MOVING: "MOVING",
} as const satisfies Record<EquipmentPlacementType, string>;

export type EquipmentPlacementStatus = NonNullable<
  NonNullable<GetEquipmentResponse["placement"]>["placementStatus"]
>;
export const EquipmentPlacementStatus = {
  IN_USE: "IN_USE",
  STORED: "STORED",
  MOVING: "MOVING",
  TO_BE_DISPOSED: "TO_BE_DISPOSED",
  NOT_PLACED: "NOT_PLACED",
} as const satisfies Record<EquipmentPlacementStatus, string>;

export type EquipmentPlacementVerificationStatus = NonNullable<
  NonNullable<GetEquipmentResponse["placement"]>["placementVerificationStatus"]
>;
export const EquipmentPlacementVerificationStatus = {
  OK: "OK",
  MISMATCH: "MISMATCH",
  UNVERIFIED: "UNVERIFIED",
} as const satisfies Record<EquipmentPlacementVerificationStatus, string>;

export type EquipmentPlacementVerificationMethod = NonNullable<
  NonNullable<GetEquipmentResponse["placement"]>["verificationMethod"]
>;
export const EquipmentPlacementVerificationMethod = {
  PHYSICAL_CHECK: "PHYSICAL_CHECK",
  SCAN: "SCAN",
  PHOTO_RECORD: "PHOTO_RECORD",
} as const satisfies Record<EquipmentPlacementVerificationMethod, string>;

export type EquipmentOperationalStatus = NonNullable<
  NonNullable<
    NonNullable<GetEquipmentResponse["lifecycle"]>["operationalStatus"]
  >
>;
export const EquipmentOperationalStatus = {
  ACTIVE: "ACTIVE",
  STOPPED: "STOPPED",
  ON_LOAN: "ON_LOAN",
} as const satisfies Record<EquipmentOperationalStatus, string>;

export type EquipmentDisposalMethod = NonNullable<
  NonNullable<GetEquipmentResponse["lifecycle"]>["disposalMethod"]
>;
export const EquipmentDisposalMethod = {
  RECYCLING: "RECYCLING",
  PHYSICAL_DESTRUCTION: "PHYSICAL_DESTRUCTION",
  VENDOR_REQUEST: "VENDOR_REQUEST",
} as const satisfies Record<EquipmentDisposalMethod, string>;

export type EquipmentWipeMethod = NonNullable<
  NonNullable<GetEquipmentResponse["lifecycle"]>["wipeMethod"]
>;
export const EquipmentWipeMethod = {
  PHYSICAL_DESTRUCTION: "PHYSICAL_DESTRUCTION",
  DOD_STANDARD: "DOD_STANDARD",
  ENCRYPTION_DELETION: "ENCRYPTION_DELETION",
} as const satisfies Record<EquipmentWipeMethod, string>;

/**
 * GET /v2/equipment (APIスキーマ準拠)
 */
export type GetEquipmentListQuery = ApiSearchQuery<"/v2/equipment", "get">;
export type GetEquipmentListResponse = ApiResponseBody<"/v2/equipment", "get">;
export type EquipmentListItem = GetEquipmentListResponse["items"][0];

export const useGetEquipmentList = (query: GetEquipmentListQuery) => {
  return useQuery({
    queryKey: EQUIPMENT_API_KEYS.list(query),
    queryFn: async () => {
      const res = await apiClient.GET("/v2/equipment", {
        params: { query },
      });
      return getDataOrThrowApiError(res);
    },
  });
};

/**
 * GET /v2/equipment-count (APIスキーマ準拠)
 */
export type GetEquipmentCountQuery = ApiSearchQuery<
  "/v2/equipment-count",
  "get"
>;

export type GetEquipmentCountResponse = ApiResponseBody<
  "/v2/equipment-count",
  "get"
>;

export const useGetEquipmentCount = (query: GetEquipmentCountQuery) => {
  return useQuery({
    queryKey: EQUIPMENT_API_KEYS.count(query),
    queryFn: async () => {
      const res = await apiClient.GET("/v2/equipment-count", {
        params: { query },
      });
      return getDataOrThrowApiError<GetEquipmentCountResponse>(res);
    },
  });
};

/**
 * GET /v2/equipment/{equipmentId} (APIスキーマ準拠)
 */
export type GetEquipmentResponse = ApiResponseBody<
  "/v2/equipment/{equipmentId}",
  "get"
>;

export const useGetEquipment = (equipmentId: string) => {
  return useSuspenseQuery({
    queryKey: EQUIPMENT_API_KEYS.detail(equipmentId),
    queryFn: async () => {
      const res = await apiClient.GET("/v2/equipment/{equipmentId}", {
        params: { path: { equipmentId } },
      });
      return getDataOrThrowApiError(res);
    },
  });
};

/**
 * POST /v2/equipment
 */
export type PostEquipmentRequest = ApiRequestBody<"/v2/equipment", "post">;
export type PostEquipmentResponse = ApiResponseBody<"/v2/equipment", "post">;

export const usePostEquipment = () => {
  return useMutation({
    mutationFn: async (
      data: PostEquipmentRequest,
    ): Promise<PostEquipmentResponse> => {
      const res = await apiClient.POST("/v2/equipment", {
        body: data,
      });
      return getDataOrThrowApiError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_API_KEYS.all });
    },
  });
};

/**
 * PATCH /v2/equipment/{equipmentId}
 */
export type PatchEquipmentRequest = ApiRequestBody<
  "/v2/equipment/{equipmentId}",
  "patch"
>;
export type PatchEquipmentResponse = ApiResponseBody<
  "/v2/equipment/{equipmentId}",
  "patch"
>;

export const usePatchEquipment = (equipmentId: string) => {
  return useMutation({
    mutationFn: async (
      data: PatchEquipmentRequest,
    ): Promise<PatchEquipmentResponse> => {
      const res = await apiClient.PATCH("/v2/equipment/{equipmentId}", {
        params: { path: { equipmentId } },
        body: data,
      });
      return getDataOrThrowApiError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_API_KEYS.all });
    },
  });
};

/**
 * Export equipment to CSV
 */
export type GetEquipmentCsvQuery = Omit<
  ApiSearchQuery<"/v2/equipment-csv", "get">,
  "mode" | "encoding"
>;
export type GetEquipmentCsvResponse = ApiResponseBody<
  "/v2/equipment-csv",
  "get"
>;
export const useGetEquipmentCsv = (query: GetEquipmentCsvQuery) => {
  return useMutation({
    mutationKey: EQUIPMENT_API_KEYS.export,
    mutationFn: async (): Promise<GetEquipmentCsvResponse> => {
      const res = await apiClient.GET("/v2/equipment-csv", {
        params: { query: { mode: "view", encoding: "UTF-8", ...query } },
      });
      return getDataOrThrowApiError(res);
    },
    onSuccess: async (data) => {
      const csvData = data as GetEquipmentCsvResponse;
      if (csvData?.url && csvData?.mode) {
        const filename = `equipment-export-${new Date().toISOString().split("T")[0]}.csv`;
        await downloadFile(csvData.url, filename);
      }
    },
  });
};

/** POST /v2/equipment-types */
export type PostEquipmentTypeRequest = ApiRequestBody<
  "/v2/equipment-types",
  "post"
>;
export type PostEquipmentTypeResponse = ApiResponseBody<
  "/v2/equipment-types",
  "post"
>;

export const usePostEquipmentType = () => {
  return useMutation({
    mutationFn: async (
      data: PostEquipmentTypeRequest,
    ): Promise<PostEquipmentTypeResponse> => {
      const res = await apiClient.POST("/v2/equipment-types", {
        body: data,
      });
      return getDataOrThrowApiError(res);
    },
  });
};

/** PATCH /v2/equipment-types/:id */
export type PatchEquipmentTypeRequest = ApiRequestBody<
  "/v2/equipment-types/{id}",
  "patch"
>;
export type PatchEquipmentTypeResponse = ApiResponseBody<
  "/v2/equipment-types/{id}",
  "patch"
>;

export const usePatchEquipmentType = (id: string) => {
  return useMutation({
    mutationFn: async (
      data: PatchEquipmentTypeRequest,
    ): Promise<PatchEquipmentTypeResponse> => {
      const res = await apiClient.PATCH("/v2/equipment-types/{id}", {
        params: { path: { id } },
        body: data,
      });
      return getDataOrThrowApiError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_API_KEYS.all });
    },
  });
};

/** POST /v2/equipment-items */
export type PostEquipmentItemRequest = ApiRequestBody<
  "/v2/equipment-items",
  "post"
>;
export type PostEquipmentItemResponse = ApiResponseBody<
  "/v2/equipment-items",
  "post"
>;

export const usePostEquipmentItem = () => {
  return useMutation({
    mutationFn: async (
      data: PostEquipmentItemRequest,
    ): Promise<PostEquipmentItemResponse> => {
      const res = await apiClient.POST("/v2/equipment-items", {
        body: data,
      });
      return getDataOrThrowApiError(res);
    },
  });
};

/** PATCH /v2/equipment-items/:id */
export type PatchEquipmentItemRequest = ApiRequestBody<
  "/v2/equipment-items/{id}",
  "patch"
>;
export type PatchEquipmentItemResponse = ApiResponseBody<
  "/v2/equipment-items/{id}",
  "patch"
>;

export const usePatchEquipmentItem = (id: string) => {
  return useMutation({
    mutationFn: async (
      data: PatchEquipmentItemRequest,
    ): Promise<PatchEquipmentItemResponse> => {
      const res = await apiClient.PATCH("/v2/equipment-items/{id}", {
        params: { path: { id } },
        body: data,
      });
      return getDataOrThrowApiError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_API_KEYS.all });
    },
  });
};

/** PATCH /v2/equipment-types/:id/enable */
export const useEnableEquipmentType = (id: string) => {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await apiClient.PATCH("/v2/equipment-types/{id}/enable", {
        params: { path: { id } },
      });
      getDataOrThrowApiError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_API_KEYS.all });
    },
  });
};

/** PATCH /v2/equipment-types/:id/disable */
export const useDisableEquipmentType = (id: string) => {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await apiClient.PATCH("/v2/equipment-types/{id}/disable", {
        params: { path: { id } },
      });
      getDataOrThrowApiError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_API_KEYS.all });
    },
  });
};

/** PATCH /v2/equipment-items/:id/enable */
export const useEnableEquipmentItem = (id: string) => {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await apiClient.PATCH("/v2/equipment-items/{id}/enable", {
        params: { path: { id } },
      });
      getDataOrThrowApiError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_API_KEYS.all });
    },
  });
};

/** PATCH /v2/equipment-items/:id/disable */
export const useDisableEquipmentItem = (id: string) => {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await apiClient.PATCH("/v2/equipment-items/{id}/disable", {
        params: { path: { id } },
      });
      getDataOrThrowApiError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_API_KEYS.all });
    },
  });
};
